/**
 * 工作区相关API服务
 * 包含工作区管理、成员管理、邀请链接等功能
 */

import { fetcher } from '../base/fetcher'
import type {
  Workspace,
  GetWorkspaceMembersRequest,
  RemoveMemberRequest,
  GenerateInviteLinkRequest,
  InvitationLinkRes,
  WorkspaceMemberListRes,
  InviteMsgRes,
  GetInviteMsgRequest,
  AcceptInviteRequest,
  TransferWorkspaceRequest,
  LeaveWorkspaceRequest,
  DeleteWorkspaceRequest,
} from '../types/workspace.types'

interface GetWorkspacesResponse {
  workspaces: Workspace[]
}

interface UpdateWorkspaceRequest {
  id?: string
  name: string
  icon: string
}

/**
 * 工作区相关API路径常量
 */
export const API_PATHS = {
  /** 获取工作区列表 */
  LIST: '/workspace/getListByWorkspaceMember',
  /** 更新工作区 */
  UPDATE: '/workspace/saveOrUpdate',
  /** 获取工作区成员列表 */
  MEMBER_LIST: '/workspace/workspaceMember/list',
  /** 移除工作区成员 */
  REMOVE_MEMBER: '/workspace/workspaceMember/removeMember',
  /** 生成邀请链接 */
  GENERATE_INVITE_LINK: '/workspace/invitationLink/generateInviteLink',
  /** 获取邀请信息 */
  GET_INVITE_MSG: '/workspace/invitationLink/inviteMsg',
  /** 接受邀请 */
  ACCEPT_INVITE: '/workspace/invitationLink/acceptInvite',
  /** 转让工作区所有权 */
  TRANSFER: '/workspace/transferWorkspace',
  /** 删除工作区 */
  DELETE: '/workspace/delById',
  /** 退出工作区 */
  LEAVE: '/workspace/quitById',
} as const

/**
 * 工作区服务对象
 * 提供所有工作区相关的API调用方法
 */
export const spaceService = {
  /**
   * 获取工作区列表
   * @returns 工作区列表
   */
  getWorkspaces: () => fetcher.get<GetWorkspacesResponse>(API_PATHS.LIST),

  /**
   * 更新工作区信息
   * @param data 更新参数（工作区名称、图标等）
   * @returns 更新结果
   */
  updateWorkspace: (data: UpdateWorkspaceRequest) =>
    fetcher.post<void>(API_PATHS.UPDATE, data),

  /**
   * 获取工作区成员列表
   * @param params 查询参数（工作区ID、搜索关键词等）
   * @returns 成员列表
   */
  getWorkspaceMembers: (params: GetWorkspaceMembersRequest) =>
    fetcher.get<WorkspaceMemberListRes>(API_PATHS.MEMBER_LIST, { params }),

  /**
   * 移除工作区成员
   * @param data 移除参数（用户ID、工作区ID）
   * @returns null（无返回数据）
   */
  removeMember: (data: RemoveMemberRequest) =>
    fetcher.post<null>(API_PATHS.REMOVE_MEMBER, data),

  /**
   * 生成工作区邀请链接
   * @param data 邀请链接参数（工作区ID）
   * @returns 邀请链接信息
   */
  generateInviteLink: (data: GenerateInviteLinkRequest) =>
    fetcher.post<InvitationLinkRes>(API_PATHS.GENERATE_INVITE_LINK, data),

  /**
   * 获取邀请信息
   * @param params 查询参数（code）
   * @returns 邀请信息
   */
  getInviteMsg: (params: GetInviteMsgRequest) =>
    fetcher.get<InviteMsgRes>(API_PATHS.GET_INVITE_MSG, {
      params: { code: params.code },
    }),

  /**
   * 接受工作区邀请
   * @param data 接受邀请参数（code）
   * @returns 接受结果
   */
  acceptInvite: (data: AcceptInviteRequest) =>
    fetcher.post<void>(API_PATHS.ACCEPT_INVITE, null, {
      params: { code: data.code },
    }),

  /**
   * 转让工作区所有权
   * @param data 转让参数（工作区ID、新拥有者ID）
   * @returns 转让结果
   */
  transferWorkspace: (data: TransferWorkspaceRequest) =>
    fetcher.post<boolean>(API_PATHS.TRANSFER, data),

  /**
   * 删除工作区
   * @param data 删除参数（工作区ID）
   * @returns 删除结果
   */
  deleteWorkspace: (data: DeleteWorkspaceRequest) =>
    fetcher.post<boolean>(API_PATHS.DELETE, data),

  /**
   * 退出工作区
   * @param data 退出参数（工作区ID）
   * @returns 退出结果
   */
  leaveWorkspace: (data: LeaveWorkspaceRequest) =>
    fetcher.post<boolean>(API_PATHS.LEAVE, data),
}
